import { FetchResult } from '@apollo/client';
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormErrorMessage } from '@pendwa/common-browser/components/Form/ErrorMessage/index.js';
import { FormInputText } from '@pendwa/common-browser/components/Form/InputText/index.js';
import { FormSubmitButton } from '@pendwa/common-browser/components/Form/SubmitButton/index.js';
import { LayoutSpacer } from '@pendwa/common-browser/components/LayoutSpacer/index.js';
import { LayoutSpacerSmall } from '@pendwa/common-browser/components/LayoutSpacerSmall/index.js';
import { ContextPageData, EnumDisplayLanguage } from '@pendwa/common-browser/context/PageData/index.js';
import { extractGraphQLErrorMessage } from '@pendwa/common-browser/core/apollo/utils.js';
import { formatTZSValue } from '@pendwa/common/utils/formatCreditValue.js';
import { isDefinedAndNotNull } from '@pendwa/common/utils/isDefinedAndNotNull.js';
import { credit_voucherSchema } from '@pendwa/db/generated/zod/index.js';
import clsx from 'clsx';
import pluralize from 'pluralize';
import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { HooksApplyCreditVoucherMutation } from '../../../generated/graphql.js';
import { useApplyCreditVoucherGraphql } from '../../../hooks/user/applyCreditVoucher.js';

const schema = z
  .object({
    code: credit_voucherSchema.shape.code,
  })
  .required();

export const ApplyCreditVoucher: React.FC<{
  number_of_credits_needed?: number;
  handleSuccess: (result: FetchResult<HooksApplyCreditVoucherMutation>) => void;
  usd_tzs: number;
}> = (props) => {
  const { viewer, display_language } = React.useContext(ContextPageData);
  const { result: mutation_result, doAction } = useApplyCreditVoucherGraphql();

  if (!viewer) {
    return null;
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });

  const steps_pay_by_phone_tanzania: {
    step_number: number;
    description: string;
  }[] = [
    {
      step_number: 1,
      description: isDefinedAndNotNull(props.number_of_credits_needed)
        ? display_language === EnumDisplayLanguage.swahili
          ? `Sasa hivi unazo vocha ${viewer.credits}. Unahitaji vocha ${props.number_of_credits_needed} zaidi ili uweze kutazama.`
          : `Right now you have ${viewer.credits} ${pluralize('credit', viewer.credits)}. You need ${
              props.number_of_credits_needed
            } more ${pluralize('credit', props.number_of_credits_needed)} to watch.`
        : display_language === EnumDisplayLanguage.swahili
          ? `Sasa hivi unazo vocha ${viewer.credits}. Unaweza kuongeza vocha kupitia ukurasa huu.`
          : `Right now you have ${viewer.credits} ${pluralize(
              'credit',
              viewer.credits
            )}. You can add more credits using this page.`,
    },
    {
      step_number: 2,
      description: isDefinedAndNotNull(props.number_of_credits_needed)
        ? display_language === EnumDisplayLanguage.swahili
          ? `Tuma TZS ${formatTZSValue(
              props.number_of_credits_needed * props.usd_tzs
            )}/= kwenda kwa wakala wetu (0760 029 406) ili kununua vocha ${
              props.number_of_credits_needed
            }. Unaweza ukanunua vocha za ziada kama utaendelea kuangalia filamu na tamthilia (Vocha 1 = TZS ${props.usd_tzs}/=).`
          : `Send TZS ${formatTZSValue(
              props.number_of_credits_needed * props.usd_tzs
            )}/= to our agent (0760 029 406) to buy ${props.number_of_credits_needed} ${pluralize(
              'credit',
              props.number_of_credits_needed
            )}. You can buy more credits if you plan on watching more movies and shows (1 credit = TZS ${props.usd_tzs}/=).`
        : display_language === EnumDisplayLanguage.swahili
          ? `Tuma malipo kwenda kwa wakala wetu (0760 029 406) ili kununua vocha kwa kiasi unachokitaka. (Vocha 1 = TZS ${props.usd_tzs}/=). Unaweza ukanunua vocha za ziada kama utaendelea kuangalia filamu na tamthilia.`
          : `Send payment to our agent (0760 029 406) to buy the credits you want. (1 credit = TZS ${props.usd_tzs}/=). You can buy more credits if you plan on watching more movies and shows.`,
    },
    {
      step_number: 3,
      description:
        display_language === EnumDisplayLanguage.swahili
          ? `Wakala wetu atakutumia vocha yako.`
          : `Our agent will send you a voucher code.`,
    },
    {
      step_number: 4,
      description:
        display_language === EnumDisplayLanguage.swahili
          ? `Ingiza namba au herufi za vocha yako kwenye sanduku hapo chini kuongeza salio.`
          : `Enter voucher code below to add credits`,
    },
  ];

  return (
    <LayoutSpacer>
      <dl className="space-y-4 divide-y divide-gray-200 sm:space-y-6">
        <Disclosure as="div">
          {({ open }) => (
            <>
              <dt className="text-lg">
                <Disclosure.Button className="flex w-full items-start justify-between rounded-md border border-indigo-600 p-2 text-left text-gray-400">
                  <span className="font-medium text-gray-900">
                    {display_language === EnumDisplayLanguage.swahili
                      ? `Maelekezo ya kulipia kwa simu Tanzania. (${open ? 'Funga' : 'Bofya hapa kufungua'})`
                      : `Instructions for Paying By Phone in Tanzania. (${open ? 'Close' : 'Click to open'})`}
                  </span>
                  <span className="ml-6 flex h-7 items-center">
                    <ChevronDownIcon
                      className={clsx(open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform')}
                      aria-hidden="true"
                    />
                  </span>
                </Disclosure.Button>
              </dt>
              <Disclosure.Panel as="dd" className="mt-2 pr-12">
                <ul className="mt-4 space-y-2">
                  {steps_pay_by_phone_tanzania.map((step) => (
                    <li key={step.step_number} className="flex space-x-2">
                      <span className="h-5 w-5 flex-shrink-0 text-indigo-500">{step.step_number}</span>
                      <span
                        className="text-left text-sm text-gray-500"
                        dangerouslySetInnerHTML={{ __html: step.description }}
                      />
                    </li>
                  ))}
                </ul>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      </dl>

      <LayoutSpacer>
        {mutation_result.error && (
          <FormErrorMessage
            message={extractGraphQLErrorMessage(mutation_result.error) ?? 'Something went wrong.'}
          />
        )}

        <form
          onSubmit={handleSubmit(async (data) => {
            const action_result = await doAction({
              variables: {
                code: data.code,
              },
            });

            if (!action_result.errors && action_result.data?.applyCreditVoucher?.data?.status) {
              setTimeout(() => props.handleSuccess(action_result), 500);
            }
          })}
        >
          <LayoutSpacerSmall>
            <FormInputText
              label={
                display_language === EnumDisplayLanguage.swahili ? 'Namba/Herufi za vocha' : 'Voucher Code'
              }
              description={
                display_language === EnumDisplayLanguage.swahili ? 'Ingiza vocha.' : 'Enter the voucher code.'
              }
              input_type={'text'}
              input_name={'code'}
              register={register}
              error={errors.code?.message}
            />

            <FormSubmitButton
              label={display_language === EnumDisplayLanguage.swahili ? 'Weka Vocha' : 'Apply Voucher'}
              loading={mutation_result.loading}
            />
          </LayoutSpacerSmall>
        </form>
      </LayoutSpacer>
    </LayoutSpacer>
  );
};
